import axios from '@/plugins/axios';

/**
 * Obtiene todas las circulares con filtros y paginación
 * @param {Object} params - Parámetros de filtrado
 * @param {string} [params.search] - Término de búsqueda
 * @param {string} [params.orderBy] - Campo de ordenamiento
 * @param {string} [params.orderByMethod] - Método de orden (asc/desc)
 * @param {number} [params.perPage] - Items por página
 * @param {number} [params.status] - Estado de la circular
 * @returns {Promise} Promesa con la respuesta paginada
 */
export const fetchAllCirculars = async (params = {}) => {
  const requestParams = {
    page: params.page || null,
    search: params.search || null,
    orderBy: params.orderBy || null,
    orderByMethod: params.orderByMethod || null,
    perPage: params.perPage || null,
    status: params.status || null
  };

  // Agregar vehicle_types si está presente
  if (params.vehicle_types && Array.isArray(params.vehicle_types) && params.vehicle_types.length > 0) {
    requestParams.vehicle_types = params.vehicle_types;
  }

  // Serializar parámetros manualmente para arrays
  const paramsSerializer = (params) => {
    const parts = [];
    Object.keys(params).forEach(key => {
      const value = params[key];
      if (value === null || value === undefined) {
        return;
      }
      if (Array.isArray(value)) {
        value.forEach(item => {
          parts.push(`${encodeURIComponent(key)}[]=${encodeURIComponent(item)}`);
        });
      } else {
        parts.push(`${encodeURIComponent(key)}=${encodeURIComponent(value)}`);
      }
    });
    return parts.join('&');
  };

  const response = await axios.get('/circulars', {
    params: requestParams,
    paramsSerializer: paramsSerializer
  });
  return response.data;
};

/**
 * Obtiene todas las circulares con filtros opcionales.
 * @param {Object} params - Parámetros para filtrar las circulares.
 * @returns {Promise} Promesa con los resultados de las circulares.
 */
export const fetchCirculars = async (params = {}) => {
  const response = await axios.get('/circulars/search', { params });
  return response.data;
};

/**
 * Obtiene los temas de las circulares.
 * @returns {Promise} Promesa con los temas.
 */
export const fetchCircularTopics = async () => {
  const response = await axios.get('/circulars/topics');
  return response.data;
};

/**
 * Obtiene los tipos de vehículos disponibles.
 * @returns {Promise} Promesa con los tipos de vehículos.
 */
export const fetchVehicleTypes = async () => {
  const response = await axios.get('/circulars/vehicles-types');
  return response.data;
};


/**
 * Obtiene los detalles de una circular específica.
 * @param {number|string} circularId - ID de la circular.
 * @returns {Promise} Promesa con los detalles de la circular.
 */
export const fetchCircularDetails = async (circularId) => {
  const response = await axios.get(`/circulars/${circularId}`);
  return response.data;
};

/**
 * Obtiene los roles relacionados con circulares
 * @returns {Promise} Promesa con los roles
 */
export const fetchCircularRoles = async () => {
  const response = await axios.get('/circulars/circular-roles');
  return response.data;
};

/**
 * Crea una nueva circular
 * @param {Object} circularData - Datos de la circular
 * @returns {Promise} Promesa con la respuesta
 */
export const createCircularItem = async (circularData) => {
  const response = await axios.post('/circulars', circularData, {
    headers: {
      'Content-Type': 'multipart/form-data',
      Accept: 'application/json',
    },
  });
  return response.data;
};

/**
 * Actualiza una circular existente
 * @param {number|string} circularId - ID de la circular
 * @param {Object} circularData - Datos actualizados de la circular
 * @returns {Promise} Promesa con la respuesta
 */
export const updateCircularItem = async (circularId, circularData) => {
  console.log('Actualizando circular con ID:', circularId);
  console.log('Datos actualizados:', circularData);
  const response = await axios.post(`/circulars/${circularId}`, circularData, {
    headers: {
      'Content-Type': 'multipart/form-data',
      Accept: 'application/json',
    },
  });
  return response.data;
};

/**
 * Elimina una circular
 * @param {number|string} circularId - ID de la circular
 * @returns {Promise} Promesa con la respuesta
 */
export const deleteCircularItem = async (circularId) => {
  try {
    console.log('Eliminando circular con ID:', circularId);
    const response = await axios.delete(`/circulars/${circularId}`);
    console.log('Respuesta de eliminación exitosa:', response.data);
    return response.data;
  } catch (error) {
    console.error('Error en deleteCircularItem:', error);
    console.error('Error response:', error.response);
    throw error;
  }
};

/**
 * Obtiene las circulares con paginación (alias para fetchAllCirculars)
 * @param {Object} params - Parámetros de filtrado y paginación
 * @returns {Promise} Promesa con la respuesta paginada
 */
export const fetchCircularItems = async (params = {}) => {
  return await fetchAllCirculars(params);
};

/**
 * Exporta las circulares
 * @param {Object} params - Parámetros de filtrado para la exportación
 * @returns {Promise} Promesa con el archivo de exportación
 */
export const exportCircularItems = async (params = {}) => {
  const response = await axios.get('/circulars/export', {
    params,
    responseType: 'blob'
  });
  return response;
};